import { useState } from "react";
import { X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const PageHeader = ({ kicker, title, description, children }) => (
  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-8">
    <div>
      {kicker && <p className="kicker text-primary">{kicker}</p>}
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1" data-testid="admin-page-title">{title}</h1>
      {description && <p className="mt-2 text-muted-foreground max-w-2xl">{description}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ title, description, children, className = "", actions, testid }) => (
  <section className={`bg-card border border-border min-w-0 ${className}`} data-testid={testid}>
    {title && (
      <header className="flex items-start justify-between gap-3 px-5 pt-5">
        <div>
          <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        {actions}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const FieldLabel = ({ children, hint, warn }) => (
  <div className="flex items-baseline justify-between gap-2 mb-1.5">
    <span className="text-sm font-medium">{children}</span>
    {hint && <span className={`text-xs font-mono ${warn ? "text-destructive" : "text-muted-foreground"}`}>{hint}</span>}
  </div>
);

const badge = "inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-wider whitespace-nowrap";
const GREEN = "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900";
const AMBER = "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900";
const SLATE = "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
const BLUE = "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900";
const RED = "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900";

const STATUS = { published: ["Publicada", GREEN], scheduled: ["Programada", AMBER], draft: ["Borrador", SLATE] };
const RUN = { running: ["En curso", BLUE], success: ["Completada", GREEN], empty: ["Sin novedades", AMBER], error: ["Error", RED] };

export const StatusBadge = ({ status, testid }) => {
  const [label, cls] = STATUS[status] || STATUS.draft;
  return <span className={`${badge} ${cls}`} data-testid={testid}>{label}</span>;
};

export const RunBadge = ({ status }) => {
  const [label, cls] = RUN[status] || RUN.running;
  return <span className={`${badge} ${cls}`}>{status === "running" && <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}{label}</span>;
};

export const ORIGIN = { manual: "Manual", ai_rss: "IA · Replicada", ai_topic: "IA · Propia" };

export const SCHEMAS = [
  ["NewsArticle", "Noticia (NewsArticle)"],
  ["AnalysisNewsArticle", "Análisis (AnalysisNewsArticle)"],
  ["OpinionNewsArticle", "Opinión (OpinionNewsArticle)"],
  ["ReportageNewsArticle", "Reportaje (ReportageNewsArticle)"],
  ["Article", "Artículo (Article)"],
  ["BlogPosting", "Entrada de blog (BlogPosting)"],
];

export const DAYS = ["L", "M", "X", "J", "V", "S", "D"];

export const cadenceText = (a) => {
  if (a.cadence_type !== "daily") return a.every_hours === 1 ? "Cada hora" : `Cada ${a.every_hours} horas`;
  const key = [...(a.days || [])].sort().join(",");
  const days = { "0,1,2,3,4,5,6": "Todos los días", "0,1,2,3,4": "De lunes a viernes", "5,6": "Fines de semana" }[key] || (a.days || []).map((i) => DAYS[i]).join(" · ");
  return `${days} a las ${(a.times || []).join(", ")}`;
};

export const TagInput = ({ value = [], onChange, placeholder, testid }) => {
  const [text, setText] = useState("");
  const add = () => {
    const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...new Set([...value, ...parts])]);
    setText("");
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    } else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 border border-input bg-background px-2 py-1.5 min-h-[40px] focus-within:ring-1 focus-within:ring-ring" data-testid={testid}>
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 text-sm">
          {v}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} aria-label={`Quitar ${v}`} className="hover:text-primary"><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} onBlur={add} placeholder={value.length ? "" : placeholder}
        className="flex-1 min-w-[120px] bg-transparent text-sm outline-none py-1" data-testid={testid ? `${testid}-input` : undefined} />
    </div>
  );
};

export const ConfirmDelete = ({ title, description, onConfirm, children, testid = "confirm-delete-btn" }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-2xl">{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-cancel-btn">Cancelar</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={testid}>Eliminar</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
